/* eslint-disable eqeqeq */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import {
  ahuComponentFormsApi,
  useGetComponentCmsDocQuery,
  useGetComponentDetailQuery,
} from 'app/services/ahuComponentFormsApi';
import type { IAhuBoardSection, IAhuComponentRow } from 'models';
import {
  validateAccessoryOnChange,
  validateOnNewComponent,
  validateTunnelOnlyComponents,
} from '../validation/validationController';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { clearPerformanceJump } from 'app/slices/ahuBoardSlice';
import { focusPerformanceEnteringTemp } from './focusEnteringTemp';
import {
  SECTION_AIR_COMMIT_KEYS,
  commitSectionAirConditions,
  hasAnyKey,
  selectBoardSyncOn,
} from './commitAirConditions';
import { FormModel, useFormModel } from './autoSaveModel';
import { CatalogueFieldEngine, useCatalogueFields } from './catalogueField';
import type {
  ComponentFormShim,
  ComponentTypeConfig,
} from './componentFormTypes';
import { registerFormShim, SECTION_RENDERED_TYPES } from './componentFormTypes';
import { useJsonDiffEmit } from './jsonDiffEmit';
import { selectValueToId } from './formUtils';

/**
 * Phase 6e2a — useComponentForm: the React home of base_classes/
 * component_view.js (§49.3 — render lifecycle + field binding) over the
 * §49.2 auto-save engine. Per-type forms are config + JSX bodies on top.
 *
 * Lifecycle parity:
 *  - boot: GET ahu/<plural>/<id> (detail) + cms/1/v1.0.0/ahu/<plural>/params/
 *    (componentCms) → FormModel armed (savedState = server row) → catalogue
 *    engines → fullyLoading resolves (preloader collapses).
 *  - `section:rendered [sectionId, section2Id]` fired ONCE, 1 s after first
 *    paint, hasRating types only (§49.3 step 7 / parity 305 / D6).
 *  - First-subtab auto-open when none checked (§49.3 step 8 / parity 306) +
 *    THE §13 row 2 consumer: componentsTab.openFirstSubtabForSectionId.
 *  - Bus listeners (§49.3): `deselct:model:<id>` [sic] re-arms the
 *    auto-single-option engine; `render:blabk_off:<id>` [sic] re-renders the
 *    blank-off slot; `changed:section:<id>:eadb_temp:eawb_temp` → 1 s →
 *    refetch → ladb/lawb in-place (xhr echo, no save);
 *    `remove_component_esp_<sectionId>` → recalc (re-fires fetch:fans:esp for
 *    the own stream when apd set); `rating_mode:hide:<id>` {selected} arms
 *    componentSelectionInRateMode (§49.2).
 *  - Render side-effect storm (§49.3 step 6) — the chassis carries the
 *    cross-type pieces: er_wheel re-render / orientation_id change →
 *    `redraw:canvas`; qty_stacked change → `section:change:qty:<id>` +
 *    `changeDrainPan`; apd/air_stream delta vs the infoForEsp cache →
 *    `fetch:fans:esp` (JSON-diff gated — parity 318/334-style throttle).
 *  - Q15 shim (§50.2): all 14 members registered under
 *    window.__FORM_SHIM__[componentId]; rating-mode interplay members are
 *    chassis-seam STUBS until 6e2c (selector cut).
 */

export interface ComponentFormArgs {
  config: ComponentTypeConfig;
  row: IAhuComponentRow;
  section: IAhuBoardSection;
  /** dual-tunnel partner (energy_wheel / plate_heat_exchanger). */
  section2?: IAhuBoardSection | null;
  /** form-provided template-data getter (shim member #12). */
  dataGetterForRendering?: () => Record<string, unknown>;
  /** §49.3 ESP diff feed — fields snapshotted vs infoForEsp (apd/air_stream;
   * ewheel/phx pass the 4-way winter/summer set). */
  espInfoFields?: string[];
  /** first RENDERED subtab (auto-open target — the tmpl's first data-subtab,
   * which can differ from config.subtabs[0]: fan/filter config supersets). */
  firstSubtab?: string;
}

export interface ComponentFormApi {
  model: FormModel | null;
  /** componentCms params doc (option lists — §49.3 template data). */
  componentCms: Record<string, unknown> | undefined;
  catalogueFields: Record<string, CatalogueFieldEngine>;
  /** subtabs dict round-tripped across renders (parity 308). */
  subtabs: Record<string, boolean>;
  toggleSubtab: (name: string, open: boolean) => void;
  /** §49.3 field binding — select/text/checkbox commits (+1 s
   * minRequiredLengthChange; 'None'→null; *_id parseInt). */
  commitModelField: (name: string, raw: string | null) => void;
  commitModelCheckbox: (name: string, checked: boolean) => void;
  commitSectionField: (name: string, raw: string | null) => void;
  /** catalogue params-field edit → filter write (§49.4 binding). */
  /** raw is a select's string value, or a checkbox's BOOLEAN (legacy
   * catalogue_select_field.js:465 `updateFilter({[field]: e.currentTarget
   * .checked})` — an unchecked box filters on `false`, it does not unset). */
  commitParamsField: (targetField: string, name: string, raw: string | boolean) => void;
  /** loading state — preloader stays until first paint (§50.0 contract). */
  loading: boolean;
  /** PATCH-flight grey-out of filterFields selects (§49.2). */
  filterFieldsDisabled: boolean;
  /** rating-mode STUB seam (6e2c owns the popup; flag = shim member #3). */
  rate: boolean;
  openRatingStub: () => void;
  /** blank-off slot version (render:blabk_off:<id> consumer — bump = redraw). */
  blankOffVersion: number;
  shim: ComponentFormShim | null;
}

export function useComponentForm(args: ComponentFormArgs): ComponentFormApi {
  const { config, row, section, section2 } = args;
  const dispatch = useAppDispatch();
  const [, forceRender] = useState(0);
  const bump = useCallback(() => forceRender((v) => v + 1), []);

  const { data: detail } = useGetComponentDetailQuery({
    plural: config.plural,
    id: row.id,
  });
  // Per-type cms params doc — URL override for evap_pack (SINGULAR route +
  // oflows family) and NO fetch at all for UDC (verify 6e2a-1).
  const cmsParamsURL =
    config.cmsParamsURL ?? `/api/v1.0/cms/1/v1.0.0/ahu/${config.plural}/params/`;
  const { data: rawComponentCms } = useGetComponentCmsDocQuery(cmsParamsURL, {
    skip: !!config.noCmsParamsDoc,
  });
  // ROOT CAUSE of verify 6e2a-2 ('-----' across ALL forms): the params doc
  // is WRAPPED under the partName key ({"eliminator": {...}}, {"er_wheel":
  // {...}}, {"evap_pack": {...}}). Legacy component.<type>.model.cms unwraps
  // it before handing `componentCms` to the tmpl — mirror that here.
  const componentCms = useMemo(() => {
    if (!rawComponentCms) return rawComponentCms;
    const inner =
      (rawComponentCms[config.partName] as Record<string, unknown> | undefined) ??
      (rawComponentCms[config.type] as Record<string, unknown> | undefined);
    if (inner && typeof inner === 'object') return inner;
    const keys = Object.keys(rawComponentCms);
    if (keys.length === 1 && typeof rawComponentCms[keys[0]] === 'object') {
      return rawComponentCms[keys[0]] as Record<string, unknown>;
    }
    return rawComponentCms;
  }, [rawComponentCms, config.partName, config.type]);

  // ---- view back-reference (mutable — §49.2 view.* touches) ----
  const viewRef = useRef({
    rate: false,
    withRating: config.withRating,
    catalogueTargetFields: config.catalogueFields.map((f) => f.targetField),
    disableFilterFields: () => undefined as void,
    enableFilterFields: () => undefined as void,
    render: () => undefined as void,
  });
  viewRef.current.render = bump;

  const [filterFieldsDisabled, setFilterFieldsDisabled] = useState(false);
  viewRef.current.disableFilterFields = () => setFilterFieldsDisabled(true);
  viewRef.current.enableFilterFields = () => {
    setFilterFieldsDisabled(false);
    bump(); // legacy enableFilterFields ends with view.render() (§49.2)
  };

  // ---- the §49.2 model ----
  const model = useFormModel(
    () =>
      detail
        ? new FormModel({
            id: row.id,
            classID: config.classID,
            type: config.type,
            initialAttributes: { ...row, ...detail },
            validatedFields: config.validatedFields,
            saveFn: (changes) =>
              dispatch(
                ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
                  plural: config.plural,
                  id: row.id,
                  data: changes,
                })
              ).unwrap() as Promise<Record<string, unknown>>,
            validation: {
              validateTunnelOnlyComponents: (sectionId) =>
                void dispatch(validateTunnelOnlyComponents({ sectionId })),
              validateOnSectionChange: (componentId) =>
                void dispatch(validateOnNewComponent(componentId)),
              validateAccessoryOnChange: (componentId) =>
                void dispatch(validateAccessoryOnChange(componentId)),
            },
            viewRef: viewRef.current,
          })
        : null,
    // ONE model per row — a later detail refetch (eadb listener forceRefetch)
    // must NOT rebuild the model: legacy model.fetch() merges attrs in place;
    // the listeners here set the fresh fields manually (xhr echo).
    [row.id, !detail]
  );

  // Arm the pipeline once the first server state is in (listenToOnce sync).
  useEffect(() => {
    if (model) model.armAutoSave();
  }, [model]);

  const catalogueFields = useCatalogueFields({
    configs: config.catalogueFields,
    model,
    dispatch,
    viewRef: viewRef.current,
  });

  const loading = !model || (!config.noCmsParamsDoc && !componentCms);

  // ---- subtab dict (parity 308; multiple open, state survives renders) ----
  const [subtabs, setSubtabs] = useState<Record<string, boolean>>({});
  const toggleSubtab = useCallback((name: string, open: boolean) => {
    setSubtabs((prev) => ({ ...prev, [name]: open }));
  }, []);

  // First-subtab auto-open when none checked (parity 306) + §13 row 2
  // consumer (componentsTab.openFirstSubtabForSectionId — api.section.select
  // opens the component form's FIRST subtab on every select).
  const openFirstFor = useAppSelector(
    (s) => s.ahuBoard.componentsTab.openFirstSubtabForSectionId
  );
  const firstSubtab = args.firstSubtab ?? config.subtabs[0];
  useEffect(() => {
    if (loading) return;
    setSubtabs((prev) => {
      if (Object.values(prev).some(Boolean)) return prev;
      return { ...prev, [firstSubtab]: true };
    });
  }, [loading, firstSubtab]);
  useEffect(() => {
    if (loading) return;
    if (
      openFirstFor != null &&
      (openFirstFor === section.id || openFirstFor === section2?.id)
    ) {
      setSubtabs((prev) =>
        prev[firstSubtab] ? prev : { ...prev, [firstSubtab]: true }
      );
    }
  }, [openFirstFor, loading, section.id, section2?.id, firstSubtab]);

  // performanceJump — the chart's SYNCHRONIZE CONDITIONS jump (no legacy
  // equivalent). Declared AFTER the first-subtab effect on purpose: both run
  // in the same commit when the jump arrives together with a `select:section`
  // (which sets openFirstSubtabForSectionId), and the jump is what the user
  // asked for — so it replaces the dict rather than adding to it, landing
  // Performance at the top of an otherwise collapsed form instead of below
  // whatever the select opened. Ignored when this form has no Performance
  // subtab, which would otherwise leave the form with nothing open at all.
  //
  // ★ CONSUMED, not just read: the clear is what stops it replaying. Without
  // it every later return to this section would re-open Performance and
  // close the first subtab for the rest of the board session, because the
  // form unmounts on each section/tab switch (TabComponents' perf rule) and
  // remounts with an empty subtab dict (review round 2 blocker 1).
  const performanceJump = useAppSelector((s) => s.ahuBoard.performanceJump);
  const [performanceFocusTick, setPerformanceFocusTick] = useState(0);
  useEffect(() => {
    if (loading || !performanceJump || performanceJump.accessoryId != null) return;
    const { sectionId } = performanceJump;
    if (sectionId !== section.id && sectionId !== section2?.id) return;
    if (!config.subtabs.includes('performance')) return;
    setSubtabs({ performance: true });
    // ★ EVERY jump focuses, not only one that opens the subtab. Reopening
    // the chart and pressing SYNCHRONIZE CONDITIONS again on a section the
    // user never left leaves Performance already open, so `FormSubtab`'s
    // closed→open transition never fires and the caret stayed where it was
    // (reported 2026-10-22). The tick below is the explicit arrival that the
    // plenum and UDA paths get from their `focusToken`.
    setPerformanceFocusTick((n) => n + 1);
    dispatch(clearPerformanceJump());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceJump, loading, section.id, section2?.id]);

  // Runs in the commit AFTER the one above — `setSubtabs` is batched with the
  // tick, so by here the Performance group is rendered whether it had to be
  // opened or was open already. Scoped to this form's own row shell
  // (`TabComponents` owns that id) rather than to a subtab id, which is
  // composed per form and not this hook's to know.
  useEffect(() => {
    if (!performanceFocusTick) return;
    focusPerformanceEnteringTemp(document.getElementById(`forms_component_${args.row.id}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceFocusTick]);

  // ---- section:rendered — ONCE, +1 s after first paint, hasRating only ----
  const sectionRenderedSent = useRef(false);
  useEffect(() => {
    if (loading || sectionRenderedSent.current) return undefined;
    sectionRenderedSent.current = true;
    if (!SECTION_RENDERED_TYPES.has(config.partName) && !SECTION_RENDERED_TYPES.has(config.type))
      return undefined;
    const timer = window.setTimeout(() => {
      ahuBoardBus.emit(
        'section:rendered',
        [section.id, section2?.id].filter((id): id is number => id != null)
      );
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [loading, config.partName, config.type, section.id, section2?.id]);

  // ---- blank-off re-render hook (render:blabk_off:<id> — typo load-bearing) ----
  const [blankOffVersion, setBlankOffVersion] = useState(0);
  useEffect(() => {
    const offs = [section.id, section2?.id]
      .filter((id): id is number => id != null)
      .map((id) =>
        ahuBoardBus.on(`render:blabk_off:${id}` as `render:blabk_off:${number}`, () =>
          setBlankOffVersion((v) => v + 1)
        )
      );
    return () => offs.forEach((off) => off());
  }, [section.id, section2?.id]);

  // ---- §49.3 dispatcher listeners ----
  useEffect(() => {
    if (!model) return undefined;
    const offs: Array<() => void> = [];

    // rating selection commit marker (§49.2 — forces tunnel validation later).
    offs.push(
      ahuBoardBus.on(
        `rating_mode:hide:${row.id}` as `rating_mode:hide:${number}`,
        (...busArgs: unknown[]) => {
          const res = busArgs[0] as { selected?: boolean } | undefined;
          if (res?.selected) {
            model.componentSelectionInRateMode = true;
            model.markUserAction(); // rating-popup commit = user gesture
          }
        }
      )
    );

    // deselct:model:<id> [sic] → re-arm auto-single-option for that field.
    offs.push(
      ahuBoardBus.on(
        `deselct:model:${row.id}` as `deselct:model:${number}`,
        (...busArgs: unknown[]) => {
          autoSelectAllowed.current = true;
          autoSelectAllowedField.current = String(busArgs[0] ?? '');
        }
      )
    );

    // <partName>:model:change → render (selector commit repaint).
    offs.push(
      ahuBoardBus.on(
        `${config.partName}:model:change` as `${string}:model:change`,
        () => bump()
      )
    );

    // changed:section:<id>:eadb_temp:eawb_temp → 1 s → refetch → in-place
    // ladb/lawb (xhr echo — NO save, §49.3).
    offs.push(
      ahuBoardBus.on(
        `changed:section:${section.id}:eadb_temp:eawb_temp` as `changed:section:${number}:eadb_temp:eawb_temp`,
        () => {
          window.setTimeout(() => {
            void dispatch(
              ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
                { plural: config.plural, id: row.id },
                { subscribe: false, forceRefetch: true }
              )
            )
              .unwrap()
              .then((fresh) => {
                model.set(
                  { ladb_temp: fresh.ladb_temp, lawb_temp: fresh.lawb_temp },
                  { xhr: true }
                );
                bump();
              })
              .catch(() => undefined);
          }, 1000);
        }
      )
    );

    // remove_component_esp_<sectionId> → recalc (own-stream fetch:fans:esp
    // when apd set — §49.3).
    offs.push(
      ahuBoardBus.on(
        `remove_component_esp_${section.id}` as `remove_component_esp_${number}`,
        () => {
          if (model.get('apd') != null && model.get('air_stream_id') != null) {
            ahuBoardBus.emit('fetch:fans:esp', {
              air_stream_ids: [Number(model.get('air_stream_id'))],
            });
          }
        }
      )
    );

    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, row.id, section.id, config.partName, config.plural]);

  // ---- render side-effect storm (§49.3 step 6 — chassis half) ----
  const emitIfChanged = useJsonDiffEmit();
  const prevOrientation = useRef<unknown>(undefined);
  useEffect(() => {
    if (!model) return;
    // orientation_id change → redraw:canvas
    const orientation = model.get('orientation_id');
    if (
      prevOrientation.current !== undefined &&
      prevOrientation.current != orientation
    ) {
      ahuBoardBus.emit('redraw:canvas');
    }
    prevOrientation.current = orientation;
  });
  // er_wheel re-render → redraw:canvas (§49.3 step 6)
  useEffect(() => {
    if (!model || config.partName !== 'er_wheel') return;
    ahuBoardBus.emit('redraw:canvas');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.getVersion(), config.partName]);

  // apd / air_stream delta vs infoForEsp cache → fetch:fans:esp (JSON-diff
  // gated; ewheel/phx pass the 4-way field set — parity 318).
  useEffect(() => {
    if (!model) return;
    const fields = args.espInfoFields ?? ['apd', 'air_stream_id'];
    const info: Record<string, unknown> = {};
    fields.forEach((f) => {
      info[f] = model.get(f);
    });
    emitIfChanged(`esp:${row.id}`, info, () => {
      const streamIds = fields
        .filter((f) => f.includes('air_stream'))
        .map((f) => Number(model.get(f)))
        .filter((v) => Number.isFinite(v));
      ahuBoardBus.emit('fetch:fans:esp', {
        air_stream_ids: streamIds.length
          ? streamIds
          : [Number(model.get('air_stream_id'))].filter((v) => Number.isFinite(v)),
      });
    });
  });

  // ---- changeSelectValue arming (§49.3) ----
  const autoSelectAllowed = useRef(config.alwaysAllowAutoSelect ?? false);
  const autoSelectAllowedField = useRef<string | null>(null);

  // ---- §49.3 field binding ----
  const commitModelField = useCallback(
    (name: string, raw: string | null) => {
      if (!model) return;
      model.markUserAction(); // user gesture — saves now allowed (6e2a-3)
      let value: unknown = raw;
      if (raw === null || raw === '' || raw === 'None') value = null;
      else if (name.endsWith('_id')) value = selectValueToId(raw);
      model.set({ [name]: value });
      // qty_stacked → drain-pan + section qty cascades (§49.3 / parity 319).
      if (name === 'qty_stacked') {
        ahuBoardBus.emit('changeDrainPan');
        ahuBoardBus.emit(
          `section:change:qty:${section.id}` as `section:change:qty:${number}`
        );
      }
      window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
      bump();
    },
    [model, section.id, bump]
  );

  const commitModelCheckbox = useCallback(
    (name: string, checked: boolean) => {
      if (!model) return;
      model.markUserAction(); // user gesture
      model.set({ [name]: checked });
      window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
      bump();
    },
    [model, bump]
  );

  // .section_field — single-section only (§49.3; PATCH wired via the 6e1
  // section queue by the TabSections owner; here we route through the same
  // mutation for the few section fields component tmpls carry).
  //
  // Dev-sync J4 — the EVAP PACK synchronize gate (section.js:200-284, the
  // :268-283 branch): an evap-pack section's _summer entering edit while
  // sync is on POSTs the EVAP PACK's own commit_air_conditions endpoint
  // (components.models[0].id in legacy ≡ this form's row.id) — only it
  // knows how to cascade a media-derived leaving shift (:196-199). Other
  // section types (filter/eliminator/silencer read-throughs) fall through
  // to the plain PATCH, exactly like legacy's plenum/evap_pack-only gate.
  const boardSyncOn = useAppSelector(selectBoardSyncOn);
  const commitSectionField = useCallback(
    (name: string, raw: string | null) => {
      const value = raw === null || raw === '' || raw === 'None' ? null : raw;
      if (
        String(section.type ?? '').toLowerCase() === 'evap_pack' &&
        boardSyncOn &&
        hasAnyKey({ [name]: value }, SECTION_AIR_COMMIT_KEYS) &&
        row?.id != null
      ) {
        void commitSectionAirConditions({
          dispatch,
          url: `/api/v1.0/ahu/evap_packs/${row.id}/commit_air_conditions`,
          sectionId: section.id,
          tunnelId: section.tunnel_id,
          changes: { [name]: value },
        });
        window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
        return;
      }
      void dispatch(
        ahuBoardFormsApi.endpoints.patchAhuSection.initiate({
          id: section.id,
          tunnelId: section.tunnel_id,
          data: { [name]: value },
        })
      );
      window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
    },
    [dispatch, section.id, section.tunnel_id, section.type, boardSyncOn, row?.id]
  );

  const commitParamsField = useCallback(
    (targetField: string, name: string, raw: string | boolean) => {
      const engine = catalogueFields[targetField];
      if (!engine || !model) return;
      model.markUserAction(); // user filter edit — auto-pick now allowed
      engine.updateFilter({ [name]: raw === 'None' ? null : raw });
    },
    [catalogueFields, model]
  );

  // ---- changeSelectValue (§49.3 component_view.js:869-921 / parity 311) ----
  // Legacy runs this at the tail of EVERY render (:870): `if (self.allow &&
  // !self.withRating) self.changeSelectValue(); else changeSelectValue(true)`
  // — "if there is only one option in the selection list, select it
  // automatically". `allow` is armed for a brand-new row (`data-new`) and,
  // UNCONDITIONALLY, for the silencer: `self.classID ===
  // "component.silencer.model"` (:309) — that is what `alwaysAllowAutoSelect`
  // carries in the config.
  //
  // QA 2026-09-16 — the arming refs above were ported in 6e2a but the sweep
  // itself never was, so on dev3 a freshly created Silencer section came up
  // with every select on `- -` while dev filled six of them. For mfg 1 the
  // CMS params doc holds exactly ONE row for mfg / shape / outside_wall /
  // inside_liner / media_fill_mtl / transmission_loss (series has 72 and type
  // 2 — which is why those two legitimately stay empty on both).
  //
  // The write is `model.set` only, NOT a user gesture: the params JSON reaches
  // the server on the first real edit, and the silencer re-runs this sweep on
  // every form open anyway (allow is forced), so the observable result matches
  // legacy without a PATCH-on-mount. Same call shape as the accessory variant
  // (useAccessoryForm §49.6:1226).
  useEffect(() => {
    if (loading || !model) return;
    if (config.withRating) return; // legacy :875
    // forEmpty pass — legacy's empties-only variant has nothing to do here.
    if (!autoSelectAllowed.current) return;
    autoSelectAllowed.current = false; // legacy `self.allow = false` (:876)
    const root = document.getElementById(`forms_component_${row.id}`);
    if (!root) return;
    const paramsSourceFields = config.catalogueFields.map((f) => f.paramsSourceField);
    const autoSelect: Record<string, Record<string, unknown>> = {};
    paramsSourceFields.forEach((f) => {
      autoSelect[f] = {};
    });
    const modelField: Record<string, unknown> = {};
    const fieldExist: Record<string, boolean> = {};
    let change = false;
    root.querySelectorAll<HTMLSelectElement>('form select').forEach((select) => {
      if (select.value !== 'None' && select.value) return;
      if (select.disabled || select.classList.contains('non-clickable')) return;
      // `<comp>_params_field` → `<comp>_params` (legacy :886 classList scan).
      const field = Array.from(select.classList)
        .map((c) => c.replace('_field', ''))
        .find((c) => paramsSourceFields.includes(c));
      const raw = field ? model.get(field) : null;
      let fieldJson: Record<string, unknown> | null = null;
      if (raw) {
        try {
          fieldJson = JSON.parse(String(raw)) as Record<string, unknown>;
        } catch {
          fieldJson = null;
        }
      }
      const value = fieldJson ? fieldJson[select.name] : null;
      const options = Array.from(select.options).filter((o) => o.value !== 'None');
      const option = value != null ? options.find((o) => o.value == String(value)) : null;
      if (value != null && options.length && !option && field) {
        // stored param the CMS list no longer carries → drop it and let the
        // catalogue re-cascade (legacy :890-894).
        const catalogue = Object.values(catalogueFields).find(
          (e) => e.config.paramsSourceField === field
        );
        model.set({ [field]: JSON.stringify({}) });
        void catalogue?.checkIfThereIsOnlyOneFoundModel(true);
      } else if (options.length === 1) {
        if (field) {
          autoSelect[field][select.name] = options[0].value;
          change = true;
          fieldExist[field] = true;
          if (select.classList.contains('model_field')) modelField[select.name] = options[0].value;
        } else if (select.classList.contains('model_field')) {
          modelField[select.name] = options[0].value;
          change = true;
        }
      }
    });
    if (!change) return;
    const forSave: Record<string, unknown> = { ...modelField };
    Object.keys(autoSelect).forEach((f) => {
      if (!fieldExist[f]) return;
      // `deselct:model:<type>` narrows the re-arm to the field it cleared.
      if (autoSelectAllowedField.current && autoSelectAllowedField.current !== f) return;
      let oldData: Record<string, unknown> = {};
      try {
        oldData = (JSON.parse(String(model.get(f) ?? '{}')) as Record<string, unknown>) || {};
      } catch {
        oldData = {};
      }
      forSave[f] = JSON.stringify({ ...oldData, ...autoSelect[f] });
    });
    autoSelectAllowedField.current = null;
    model.set(forSave);
  });

  // ---- rating STUB (chassis seam — selector popup = 6e2c) ----
  const ratingRequest = useAppSelector((s) => s.ahuBoard.rating);
  const openRatingStub = useCallback(() => {
    // ★ SECOND CLICK CLOSES (legacy SelectionMode.changeToRatingMode,
    // selection_mode.js:176-183):
    //   if (self.ratingModeActive && self.ratingDialogIsOpened())
    //     self.deactivateRatingMode(...)      // → Dispatcher 'rating_mode:hide'
    //   else self.activateRatingMode();       // → 'rating_mode:activate'
    // Both openers land here — the form's own "Rating" label (RatingModeBlock)
    // and the navy `#show_rating` button, which legacy resolves to that same
    // label and clicks (tabs.navy.panel.js showRating:177-180). The React port
    // only ever emitted `activate`, and the chassis treats an activate for the
    // already-open selector as a no-op (ComponentSelectorDialog renderFromAHU),
    // so re-clicking SELECTION/RATING did nothing.
    // `ratingModeActive` is PER-COMPONENT in legacy, hence the id compare:
    // clicking a DIFFERENT component's label still switches the dialog over.
    // `ratingDialogIsOpened()` is ported verbatim (:185-187) rather than folded
    // into the store read — it keeps a stale gate from emitting a `hide` that
    // would un-collapse the shy strip while no dialog is on screen.
    if (
      ratingRequest.open?.name === config.partName &&
      ratingRequest.open?.modelId === row.id &&
      document.querySelector('.main-rotation')?.classList.contains('rotate')
    ) {
      ahuBoardBus.emit('rating_mode:hide');
      return;
    }
    // current_model.id keys the §30.4 fan-out (evap_pack_<id>:selector:<action>)
    // and lets a real selector resolve the component row + its form shim
    // (window.__FORM_SHIM__[id].model / .render). section is the legacy
    // options.section the selector reads section airflow fields off. Parity:
    // component_selector.js renderFromAHU options {current_model, section}.
    // `section2` is the dual-tunnel partner (DUAL_TYPES — rotary wheel / plate
    // HX sit on a section in EACH tunnel). Legacy needs no equivalent because
    // its selectors walk `current_model.view.sections` and get both; React only
    // ever shipped the primary, so the ewheel selector could render just ONE
    // "Available Airway W x H" row where legacy renders one per tunnel.
    ahuBoardBus.emit('rating_mode:activate', {
      name: config.partName,
      loading: true,
      current_model: { id: row.id },
      section: section as unknown as Record<string, unknown>,
      section2: (section2 ?? null) as unknown as Record<string, unknown> | null,
    });
    ahuBoardBus.emit('open_rating_popup');
  }, [config.partName, ratingRequest.open, row.id, section, section2]);
  const lastRatingToken = useRef(0);
  useEffect(() => {
    if (
      ratingRequest.requestedSectionId != null &&
      ratingRequest.requestToken !== lastRatingToken.current &&
      (ratingRequest.requestedSectionId === section.id ||
        ratingRequest.requestedSectionId === section2?.id)
    ) {
      lastRatingToken.current = ratingRequest.requestToken;
      if (config.withRating || config.catalogueFields.length) openRatingStub();
    }
  }, [
    ratingRequest,
    section.id,
    section2?.id,
    config.withRating,
    config.catalogueFields.length,
    openRatingStub,
  ]);

  // ---- Q15 shim registration (§50.2 / parity 342) ----
  const fullyLoadingResolve = useRef<(() => void) | null>(null);
  const fullyLoading = useMemo(
    () =>
      new Promise<void>((res) => {
        fullyLoadingResolve.current = res;
      }),
    []
  );
  useEffect(() => {
    if (!loading) fullyLoadingResolve.current?.();
  }, [loading]);

  // ★ Shim #9 — fan_rating_curves_model is a SETTABLE ref (the fan selector
  // stamps it on Apply; FanForm nulls it on a manual master-fan pick — legacy
  // onManualModeSelection). Backed by a ref so the stamp survives shim re-creates.
  const fanRatingCurvesRef = useRef<unknown | null>(null);

  const shim = useMemo<ComponentFormShim | null>(() => {
    if (!model) return null;
    const view = viewRef.current;
    const shimObj: ComponentFormShim = {
      render: () => bump(),
      model,
      get rate() {
        return view.rate;
      },
      set rate(v: boolean) {
        view.rate = v;
      },
      catalogueFields: {},
      get fan_rating_curves_model() {
        return fanRatingCurvesRef.current;
      },
      set fan_rating_curves_model(v: unknown | null) {
        fanRatingCurvesRef.current = v;
      },
      // members #10/#11/#13 are accessory-form members (6e2b) — stubs keep
      // the frozen signature for selectors that probe them on components.
      createPosition: async () => undefined,
      autoSize: () => false,
      dataGetterForRendering: () => args.dataGetterForRendering?.() ?? {},
      defaultFaceVelocity: '1200',
      catalogueFieldsInitializingPromise: fullyLoading,
      fullyLoading,
    };
    return shimObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, fullyLoading]);

  useEffect(() => {
    if (!shim) return undefined;
    Object.keys(catalogueFields).forEach((target) => {
      shim.catalogueFields[target] = catalogueFields[target].toShim();
    });
    return registerFormShim(row.id, shim);
  }, [shim, catalogueFields, row.id]);

  return {
    model,
    componentCms,
    catalogueFields,
    subtabs,
    toggleSubtab,
    commitModelField,
    commitModelCheckbox,
    commitSectionField,
    commitParamsField,
    loading,
    filterFieldsDisabled,
    rate: viewRef.current.rate,
    openRatingStub,
    blankOffVersion,
    shim,
  };
}
